import React, { useEffect, useState } from 'react'
import { Modal, Form, Button, Spinner, Alert } from 'react-bootstrap'
import { IUser } from './types' 
import { currencies } from '../../_utils'

interface UserModalProps {
    show: boolean
    onHide: () => void
    onSubmit: (user: Omit<IUser, 'id'>, file?: File) => Promise<void>
    currentUser: IUser | null
    availableUsers: IUser[]
}

const UserModal: React.FC<UserModalProps> = ({ show, onHide, currentUser, onSubmit, availableUsers }) => {
    const [username, setUsername] = useState<string>('')
    const [kind, setKind] = useState<'advertiser' | 'publisher'>('advertiser')

    const [feedEnabled, setFeedEnabled] = useState<boolean>(true)
    const [marketingEnabled, setMarketingEnabled] = useState<boolean>(true)

    const [byeClickEnabled, setByeClickEnabled] = useState<boolean>(false)
    const [partnershipsEnabled, setPartnershipsEnabled] = useState<boolean>(false)
    const [growthOpportunitiesEnabled, setGrowthOpportunitiesEnabled] = useState<boolean>(false)
    const [apiEnabled, setApiEnabled] = useState(false)
    const [apiDailyLimit, setApiDailyLimit] = useState('')

    const [currency, setCurrency] = useState<string>('EUR')
    const [isAdmin, setIsAdmin] = useState<boolean>(false)
    const [switchAccessUsernames, setSwitchAccessUsernames] = useState<string[]>([])
    const [switchAccessSearch, setSwitchAccessSearch] = useState<string>('')
    const [loading, setLoading] = useState<boolean>(false)
    const [error, setError] = useState<string | null>(null)
    const [logoFile, setLogoFile] = useState<File | undefined>(undefined)

    const [usernameError, setUsernameError] = useState<string | null>(null)
    const [kindError, setKindError] = useState<string | null>(null)
    const [currencyError, setCurrencyError] = useState<string | null>(null)

    useEffect(() => {
        if (currentUser) {
            setUsername(currentUser.username)
            setKind(currentUser.kind)

            // Convert disabled → enabled in UI
            setFeedEnabled(!(currentUser.feed?.disabled ?? false))
            setMarketingEnabled(!(currentUser.marketing?.disabled ?? false))

            // ByeClick
            setByeClickEnabled(currentUser.byClick?.enabled ?? false)
            setPartnershipsEnabled(currentUser.partnerships?.enabled ?? false)
            setGrowthOpportunitiesEnabled(currentUser.growthOpportunities?.enabled ?? false)
            setApiEnabled(currentUser.api?.enabled ?? false)
            setApiDailyLimit(currentUser.api?.daily_request_limit?.toString() ?? '')

            setCurrency(currentUser.settings.currency || 'EUR')
            setIsAdmin(currentUser.isAdmin || false)
            setSwitchAccessUsernames(currentUser.switchAccessUsernames || [])
        } else {
            setUsername('')
            setKind('advertiser')
            setFeedEnabled(true)
            setMarketingEnabled(true)
            setByeClickEnabled(false)
            setPartnershipsEnabled(false)
            setGrowthOpportunitiesEnabled(false)
            setApiEnabled(false)
            setApiDailyLimit('')
            setCurrency('EUR')
            setIsAdmin(false)
            setSwitchAccessUsernames([])
        }

        setSwitchAccessSearch('')

        if (!show) {
            setError(null)
            setUsernameError(null)
            setKindError(null)
            setCurrencyError(null)
        }
    }, [currentUser, show])

    const validateForm = () => {
        let isValid = true
        setUsernameError(null)
        setKindError(null)
        setCurrencyError(null)

        if (!username.trim()) {
            setUsernameError('Username is required')
            isValid = false
        }

        if (!kind) {
            setKindError('Kind is required')
            isValid = false
        }

        if (!currency) {
            setCurrencyError('Currency is required')
            isValid = false
        }

        if (!isAdmin && apiDailyLimit !== '' && (!/^\d+$/.test(apiDailyLimit) || Number(apiDailyLimit) < 1 || Number(apiDailyLimit) > 1000000)) {
            setError('API requests per day must be a whole number from 1 to 1,000,000, or left empty.')
            isValid = false
        }

        return isValid
    }

    const handleSave = async () => {
        setError(null)

        if (!validateForm()) return

        setLoading(true)

        const normalizedPartnershipsEnabled = partnershipsEnabled && (kind === 'advertiser' || byeClickEnabled)
        const normalizedGrowthOpportunitiesEnabled = growthOpportunitiesEnabled && kind === 'publisher' && byeClickEnabled

        const newUser: Omit<IUser, 'id'> = {
            ...(currentUser || {}),
            username,
            kind,
            settings: {
                currency,
            },
            isAdmin,
            switchAccessUsernames: isAdmin ? [] : switchAccessUsernames,

            feed: { disabled: !feedEnabled },
            marketing: { disabled: !marketingEnabled },
            byClick: { enabled: byeClickEnabled },
            partnerships: { enabled: normalizedPartnershipsEnabled },
            growthOpportunities: { enabled: normalizedGrowthOpportunitiesEnabled },
            api: { enabled: apiEnabled && !isAdmin, daily_request_limit: isAdmin || apiDailyLimit === '' ? null : Number(apiDailyLimit) },
        }

        try {
            await onSubmit(newUser, logoFile)
            setLoading(false)
            onHide()
        } catch (err) {
            setLoading(false)
            setError("Failed to save user. Please try again. " + err)
        }
    }

    const handleClose = () => {
        setError(null)
        setUsernameError(null)
        setKindError(null)
        setCurrencyError(null)
        onHide()
    }

    const selectableUsers = availableUsers
        .filter(user => user.username !== username)
        .filter(user => user.username.toLowerCase().includes(switchAccessSearch.toLowerCase()))
        .sort((a, b) => a.username.localeCompare(b.username))

    const toggleSwitchAccess = (targetUsername: string) => {
        setSwitchAccessUsernames(prev =>
            prev.includes(targetUsername)
                ? prev.filter(username => username !== targetUsername)
                : [...prev, targetUsername].sort((a, b) => a.localeCompare(b))
        )
    }

    const canManagePartnershipsAddon = kind === 'advertiser' || byeClickEnabled

    return (
        <Modal show={show} onHide={handleClose} size='lg'>
            <Modal.Header closeButton>
                <Modal.Title>{currentUser ? 'Edit User' : 'Create User'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}

                <Form onSubmit={(e) => { e.preventDefault(); handleSave() }}>
                    
                    {/* Username */}
                    <Form.Group className="mb-3">
                        <Form.Label htmlFor="username">Username</Form.Label>
                        <Form.Control
                            type="text"
                            id="username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            required
                            disabled={!!currentUser || loading}
                            isInvalid={!!usernameError}
                        />
                        {usernameError && (
                            <Form.Control.Feedback type="invalid">
                                {usernameError}
                            </Form.Control.Feedback>
                        )}
                    </Form.Group>

                    {/* Kind */}
                    <Form.Group className="mb-3">
                        <Form.Label htmlFor="kind">Kind</Form.Label>
                        <Form.Select
                            id="kind"
                            value={kind}
                            onChange={(e) => setKind(e.target.value as any)}
                            required
                            disabled={!!currentUser || loading}
                            isInvalid={!!kindError}
                        >
                            <option value="advertiser">Advertiser</option>
                            <option value="publisher">Publisher</option>
                        </Form.Select>
                        {kindError && (
                            <Form.Control.Feedback type="invalid">
                                {kindError}
                            </Form.Control.Feedback>
                        )}
                    </Form.Group>

                    {/* Currency */}
                    <Form.Group className="mb-3">
                        <Form.Label htmlFor="currency">Currency</Form.Label>
                        <Form.Select
                            id="currency"
                            value={currency}
                            onChange={(e) => setCurrency(e.target.value)}
                            required
                            disabled={loading}
                            isInvalid={!!currencyError}
                        >
                            {currencies.map(c => (
                                <option key={c} value={c}>{c}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>

                    {/* Logo */}
                    <Form.Group className="mb-3">
                        <Form.Label>Logo</Form.Label>
                        <Form.Control
                            type="file"
                            accept="image/*"
                            onChange={(e: any) => setLogoFile(e.target.files?.[0] || undefined)}
                            disabled={loading}
                        />
                    </Form.Group>

                    {/* Feed Enabled */}
                    <Form.Group className="mb-3">
                        <Form.Check
                            type="checkbox"
                            id="feedEnabled"
                            label="Feed Enabled"
                            checked={feedEnabled}
                            onChange={() => setFeedEnabled(!feedEnabled)}
                            disabled={loading}
                        />
                    </Form.Group>

                    {/* Marketing Enabled */}
                    <Form.Group className="mb-3">
                        <Form.Check
                            type="checkbox"
                            id="marketingEnabled"
                            label="Marketing Enabled"
                            checked={marketingEnabled}
                            onChange={() => setMarketingEnabled(!marketingEnabled)}
                            disabled={loading}
                        />
                    </Form.Group>

                    {/* ByeClick Enabled */}
                    <Form.Group className="mb-3">
                        <Form.Check
                            type="checkbox"
                            id="byeClickEnabled"
                            label="ByeLink Enabled"
                            checked={byeClickEnabled}
                            onChange={() => {
                                const nextByeClickEnabled = !byeClickEnabled
                                setByeClickEnabled(nextByeClickEnabled)
                                if (!nextByeClickEnabled && kind === 'publisher') {
                                    setPartnershipsEnabled(false)
                                    setGrowthOpportunitiesEnabled(false)
                                }
                            }}
                            disabled={loading}
                        />
                    </Form.Group>

                    {(kind === 'publisher' || kind === 'advertiser') && (
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                id="partnershipsEnabled"
                                label="Partnerships access (ByeLink add-on)"
                                checked={partnershipsEnabled}
                                onChange={() => setPartnershipsEnabled(!partnershipsEnabled)}
                                disabled={loading || !canManagePartnershipsAddon}
                            />
                            {kind === 'publisher' && !byeClickEnabled && (
                                <div className="text-muted small mt-1">
                                    Requires ByeLink to be enabled.
                                </div>
                            )}
                        </Form.Group>
                    )}

                    {kind === 'publisher' && (
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                id="growthOpportunitiesEnabled"
                                label="Monetization Audit access (ByeLink add-on)"
                                checked={growthOpportunitiesEnabled}
                                onChange={() => setGrowthOpportunitiesEnabled(!growthOpportunitiesEnabled)}
                                disabled={loading || !byeClickEnabled}
                            />
                            {!byeClickEnabled && (
                                <div className="text-muted small mt-1">
                                    Requires ByeLink to be enabled.
                                </div>
                            )}
                        </Form.Group>
                    )}

                    <hr />

                    {/* Admin */}
                    <Form.Group className="mb-3">
                        <Form.Check type="checkbox" id="apiEnabled" label="API access"
                            checked={apiEnabled && !isAdmin} disabled={loading || isAdmin}
                            onChange={() => setApiEnabled(!apiEnabled)} />
                        <div className="text-muted small mt-1">Allows account managers to create read-only API keys. Disabling access revokes all keys immediately.</div>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="apiDailyLimit">
                        <Form.Label>API requests per day</Form.Label>
                        <Form.Control type="number" min={1} max={1000000} step={1} value={apiDailyLimit}
                            onChange={event => setApiDailyLimit(event.target.value)} disabled={loading || isAdmin} placeholder="No daily limit" />
                        <Form.Text>Leave empty for no daily limit. Shared across all account keys; resets at midnight UTC. Minute and concurrency limits still apply.</Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Check
                            type="checkbox"
                            id="isAdmin"
                            label="Is Admin"
                            checked={isAdmin}
                            onChange={() => setIsAdmin(!isAdmin)}
                            disabled={loading}
                        />
                    </Form.Group>

                    {!isAdmin && (
                        <>
                            <hr />

                            <Form.Group className="mb-3">
                                <Form.Label>Switch Access</Form.Label>
                                <div className="text-muted small mb-2">
                                    Users selected here will be available in the Switch menu for this user.
                                </div>
                                <Form.Control
                                    type="text"
                                    placeholder="Search users to link"
                                    value={switchAccessSearch}
                                    onChange={(e) => setSwitchAccessSearch(e.target.value)}
                                    disabled={loading}
                                    className="mb-2"
                                />

                                <div className="d-flex flex-wrap gap-2 mb-3">
                                    {switchAccessUsernames.length ? switchAccessUsernames.map((targetUsername) => (
                                        <span key={targetUsername} className="badge bg-primary-subtle text-dark border">
                                            {targetUsername}
                                        </span>
                                    )) : (
                                        <span className="text-muted small">No linked users selected</span>
                                    )}
                                </div>

                                <div className="border rounded p-3" style={{ maxHeight: '220px', overflowY: 'auto' }}>
                                    {selectableUsers.length ? selectableUsers.map((user) => (
                                        <Form.Check
                                            key={user.id}
                                            type="checkbox"
                                            id={`switch-access-${user.id}`}
                                            label={user.username}
                                            checked={switchAccessUsernames.includes(user.username)}
                                            onChange={() => toggleSwitchAccess(user.username)}
                                            disabled={loading}
                                            className="mb-2"
                                        />
                                    )) : (
                                        <div className="text-muted small">No users match your search.</div>
                                    )}
                                </div>
                            </Form.Group>
                        </>
                    )}
                </Form>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={loading}>
                    Close
                </Button>

                <Button variant="primary" onClick={handleSave} disabled={loading}>
                    {loading ? (
                        <>
                            <Spinner as="span" animation="border" size="sm" /> Saving...
                        </>
                    ) : (
                        currentUser ? 'Save Changes' : 'Create User'
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default UserModal
